// Pure boundary logic for the Dashboard's non-destructive "New session" marker.
//
// Problem: the proxy's server-side history buffer is replayed as `history_snapshot`
// on every dashboard connect and MERGED with localStorage (by design — proxy
// restarts/reconnects must not lose data), so starting a fresh test pass shows all
// of the previous pass's rows too. A boundary is just a timestamp (ms) the user
// stamps; rows captured before it are collapsed behind an expandable "earlier"
// header instead of being deleted. This is the ONLY place boundary semantics live;
// TrafficContext.js and MainApp.js just call these functions.
//
// NOTE (2026-08-11): the boundary is no longer the whole of "New session" — that
// action now genuinely clears local state and the proxy's buffer too. The boundary
// survives as the FALLBACK for when the server-side wipe doesn't land (WS down,
// broadcast missed): the proxy would otherwise replay its history on the next
// connect, and this is what keeps those rows hidden. On a confirmed clear the
// history_cleared handler drops the boundary, since nothing is left to hide.
const BOUNDARY_KEY = 'session.boundaryTs';

// try/catch-guarded like the rest of the codebase's localStorage use (private
// browsing / quota-full / disabled storage must never throw into the UI).
export function loadBoundary() {
  try {
    const raw = localStorage.getItem(BOUNDARY_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function saveBoundary(ts) {
  try {
    localStorage.setItem(BOUNDARY_KEY, String(ts));
  } catch { /* ignore — quota/unavailable storage, boundary just won't persist */ }
}

export function clearBoundary() {
  try {
    localStorage.removeItem(BOUNDARY_KEY);
  } catch { /* ignore */ }
}

// A row with a missing/unparsable timestamp counts as AFTER the boundary — fail
// open, so a row the boundary can't place is never silently hidden. A null/
// undefined boundary means "no boundary set" — everything is current.
export function isAfterBoundary(row, boundaryTs, tsFn) {
  if (boundaryTs == null) return true;
  const t = tsFn ? tsFn(row) : 0;
  if (!t) return true;
  return t >= boundaryTs;
}

// Splits rows into { current, earlier }, preserving the input order in each bucket.
export function splitByBoundary(rows, boundaryTs, tsFn) {
  const current = [];
  const earlier = [];
  (rows || []).forEach((row) => {
    if (isAfterBoundary(row, boundaryTs, tsFn)) current.push(row);
    else earlier.push(row);
  });
  return { current, earlier };
}
